// Shared activity row formatter used by Dashboard and Activity (Wallet) pages.
// Keeps wording consistent across surfaces.
import { formatName } from './format';

/**
 * Builds the label, status tag, and type tag for a wallet/activity transaction.
 * @param {object} txn - transaction record from /api/wallet/transactions
 * @returns {{ label: string, tag: {text: string, cls: string} | null, typeTag: string | null, isIncoming: boolean, isExternal: boolean }}
 */
export function getActivityRow(txn) {
  const isIncoming = (txn.amount_cents ?? 0) >= 0;
  const niceName = formatName(txn.payer_name);
  const isExternal = txn.payment_method === 'external' || txn.flow === 'external' || txn.flow === 'external_pending';
  const splitName = txn.session_description || txn.group_name || null;
  const cleanSplit = splitName && splitName !== 'Quick Split' ? splitName : null;

  let label;
  if (niceName) {
    if (isExternal) {
      label = txn.flow === 'external_pending'
        ? `${niceName} marked as paid`
        : `${niceName} paid externally`;
    } else {
      label = isIncoming ? `${niceName} paid` : `Paid to ${niceName}`;
    }
  } else {
    label = isIncoming ? 'Payment received' : 'Withdrawal';
  }
  if (cleanSplit) label += ` • ${cleanSplit}`;

  const status = (txn.payment_status || '').toLowerCase();
  let tag = null;
  if (status === 'pending_external' || status === 'pending' || txn.flow === 'external_pending') {
    tag = { text: 'Pending', cls: 'bg-amber-50 text-amber-700 border-amber-200' };
  } else if (isExternal) {
    tag = { text: 'External', cls: 'bg-orange-50 text-orange-700 border-orange-200' };
  } else if (txn.type === 'quick_split' || txn.type === 'quick_split_payment' || status === 'completed' || status === 'succeeded' || isIncoming) {
    tag = { text: 'Paid', cls: 'bg-green-50 text-green-700 border-green-200' };
  }

  // Surface a friendly type tag only for Quick Split family transactions.
  // Legacy group transactions still appear in the All feed but no longer
  // expose the old "Group" terminology in the UI.
  const t = (txn.type || '').toLowerCase();
  let typeTag = null;
  if (t === 'quick_split' || t === 'quick_split_payment' || t === 'card_payment') typeTag = 'Quick Split';

  return { label, tag, typeTag, isIncoming, isExternal };
}
